import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";

const QuantityInput = ({
  label,
  info,
  min = 0,
  max = 0,
  isDisabled = false,
  isRequired = false,
  onValueChange,
  initialValue = 0, // Set default initialValue
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (value > max) {
      setValue(max);
      onValueChange(max);
    }
  }, [max]);

  const handleIncrement = () => {
    if (value < max) {
      setValue((prevValue) => prevValue + 1);
      onValueChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      setValue((prevValue) => prevValue - 1);
      onValueChange(value - 1);
    }
  };

  const handleCustomValue = (newValue) => {
    const clampedValue = Math.max(min, Math.min(newValue, max)); // Clamp value within min/max range
    setValue(clampedValue);
    onValueChange(clampedValue);
  };

  const handleChange = (event) => {
    const inputValue = event.target.value;
    // Handle empty input or invalid characters (optional)
    if (inputValue === "") {
      setValue(""); // Set to empty string if desired
    } else {
      const parsedValue = parseFloat(inputValue);
      if (!isNaN(parsedValue)) {
        handleCustomValue(parsedValue);
      }
    }
  };

  return (
    <div className={`QuantityInput ${isDisabled && "opacity-40"} flex flex-col gap-y-1 w-full`} {...props}>
      <div className="flex gap-x-4 items-center justify-between">
        <div className="flex gap-x-1">
          <label htmlFor="quantity" className="text-sm">
            {label}
          </label>
          {isRequired && <span className="text-warningLevelRed">*</span>}
        </div>
        <div className="flex justify-between p-px border-[1px] border-solid border-skyBlue w-24 h-10 rounded-sm">
          <input
            value={value}
            name="quantity"
            type="text"
            min={min}
            max={max}
            className="w-full border-0 text-center focus:outline-none focus:ring-0"
            onChange={handleChange}
            onBlur={handleChange} // Add validation on blur as well (optional)
          />
          <div className="flex flex-col justify-between gap-y-px">
            <button
              type="button"
              disabled={value >= max}
              className={`bg-skyBlue w-4 h-5 rounded-tr-sm flex justify-center items-center ${
                value >= max && "opacity-50"
              }`}
              onClick={handleIncrement}
            >
              <FiChevronUp className="text-white" />
            </button>
            <button
              type="button"
              disabled={value <= min}
              className={`bg-skyBlue w-4 h-5 rounded-br-sm flex justify-center items-center ${
                value <= min && "opacity-50"
              }`}
              onClick={handleDecrement}
            >
              <FiChevronDown className="text-white" />
            </button>
          </div>
        </div>
      </div>
      {info && <span className="text-sm text-gray-400">{info}</span>}
    </div>
  );
};

QuantityInput.propTypes = {
  label: PropTypes.string,
  info: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  onValueChange: PropTypes.func.isRequired
};

export default QuantityInput;
